/**
 * O que a Central mandou nesta conversa e ainda não voltou pelo monitor.
 *
 * Cada envio vira um comando na fila do servidor (o monitor executa). A tela
 * mostra o balão na hora ("enviando…"), troca pelo comando do servidor quando
 * o POST responde e relê `comandos?abertos=1` a cada 4 s enquanto houver
 * comando a caminho. O comando que sai da lista foi enviado: fica um balão
 * "enviado" até a mensagem real chegar pelo monitor (ou 1 minuto). Falhou:
 * o balão diz o motivo e oferece "Tentar de novo".
 *
 * `marcar_lida` não vira balão: a tela usa `leitura` no cabeçalho.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useAuth } from "../../../context/AuthContext";
import { enviarMensagem, listarComandosAbertos, repetirComando } from "../../../services/centralProjetos";
import { comandoJaChegou, situacaoDoComando, tipoDoComando } from "../apresentacao";

export const INTERVALO_DOS_COMANDOS_MS = 4000;
/** Quanto tempo o balão "enviado" espera a mensagem real voltar pelo monitor. */
const ESPERA_DO_ENVIADO_MS = 60000;

function abaVisivel() {
  return typeof document === "undefined" || document.visibilityState !== "hidden";
}

const ehLeitura = (comando) => tipoDoComando(comando) === "marcar_lida";
const quando = (comando) => new Date(comando.criado_em || 0).getTime() || 0;

export default function useComandosDaConversa(conversaId, { habilitado, mensagens, aoConcluir, aoMarcarLida }) {
  const { request } = useAuth();
  const [servidor, setServidor] = useState([]);
  const [locais, setLocais] = useState([]);
  const [enviados, setEnviados] = useState([]);
  const [errosDeRepeticao, setErrosDeRepeticao] = useState({});
  const [erroDaFila, setErroDaFila] = useState("");
  const servidorAtual = useRef(servidor);
  servidorAtual.current = servidor;
  const avisos = useRef({ aoConcluir, aoMarcarLida });
  avisos.current = { aoConcluir, aoMarcarLida };
  const citacoes = useRef(new Map());
  const contador = useRef(0);
  const vivo = useRef(true);

  useEffect(() => {
    vivo.current = true;
    return () => {
      vivo.current = false;
    };
  }, []);

  const carregar = useCallback(async () => {
    if (!habilitado) return;
    let lista;
    try {
      lista = await listarComandosAbertos(request, conversaId);
    } catch (falha) {
      // Leitura de fundo: a próxima volta tenta de novo; a tela avisa enquanto isso.
      if (vivo.current) setErroDaFila(falha?.message || "Não deu para conferir os envios agora.");
      return;
    }
    if (!vivo.current) return;
    setErroDaFila("");
    const abertos = new Set(lista.map((c) => c.id));
    const sairam = servidorAtual.current.filter((c) => !abertos.has(c.id) && situacaoDoComando(c) === "enviando");
    setServidor(lista);
    if (!sairam.length) return;
    const ate = Date.now() + ESPERA_DO_ENVIADO_MS;
    const envios = sairam.filter((c) => !ehLeitura(c));
    if (envios.length) {
      setEnviados((atuais) => [
        ...atuais.filter((e) => !envios.some((s) => s.id === e.id)),
        ...envios.map((c) => ({ ...c, estado: "enviado", ate })),
      ]);
      avisos.current.aoConcluir?.();
    }
    if (sairam.some(ehLeitura)) avisos.current.aoMarcarLida?.();
  }, [conversaId, habilitado, request]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const aCaminho = servidor.some((c) => situacaoDoComando(c) === "enviando") || locais.some((l) => l.estado === "enviando");
  useEffect(() => {
    if (!aCaminho) return undefined;
    const relogio = setInterval(() => {
      if (abaVisivel()) carregar();
    }, INTERVALO_DOS_COMANDOS_MS);
    return () => clearInterval(relogio);
  }, [aCaminho, carregar]);

  // O balão "enviado" sai sozinho se a mensagem demorar a voltar pelo monitor.
  useEffect(() => {
    if (!enviados.length) return undefined;
    const proximo = Math.min(...enviados.map((e) => e.ate)) - Date.now();
    const relogio = setTimeout(() => setEnviados((atuais) => atuais.filter((e) => e.ate > Date.now())), Math.max(proximo, 0));
    return () => clearTimeout(relogio);
  }, [enviados]);

  const mandar = useCallback(
    async (local) => {
      const { tipo, texto, arquivo, citada, marcarLida } = local.payload;
      try {
        const comando = await enviarMensagem(request, conversaId, {
          tipo,
          texto,
          arquivo,
          citadaId: citada?.id_externo,
          marcarLida,
        });
        if (!vivo.current) return;
        if (comando?.id !== undefined && citada) citacoes.current.set(comando.id, citada);
        setLocais((atuais) => atuais.filter((l) => l.chave !== local.chave));
        if (comando?.id !== undefined) setServidor((atuais) => [...atuais.filter((c) => c.id !== comando.id), comando]);
        carregar();
      } catch (falha) {
        if (!vivo.current) return;
        const erro = falha?.message || "Não deu para enviar agora.";
        setLocais((atuais) => atuais.map((l) => (l.chave === local.chave ? { ...l, estado: "falhou", erro } : l)));
      }
    },
    [carregar, conversaId, request],
  );

  /** `{tipo, texto, arquivo, citada, marcarLida, duracao}` → balão na hora e o POST. */
  const enviar = useCallback(
    (payload) => {
      contador.current += 1;
      const local = {
        chave: `local-${contador.current}`,
        local: true,
        estado: "enviando",
        tipo: payload.tipo,
        texto: payload.texto || "",
        arquivo_nome: payload.arquivo?.name || "",
        arquivo_tamanho: payload.arquivo?.size || 0,
        duracao: payload.duracao || 0,
        citada: payload.citada || null,
        criado_em: new Date().toISOString(),
        payload,
      };
      setLocais((atuais) => [...atuais, local]);
      return mandar(local);
    },
    [mandar],
  );

  const tentarDeNovo = useCallback(
    async (item) => {
      if (item.local) {
        setLocais((atuais) => atuais.map((l) => (l.chave === item.chave ? { ...l, estado: "enviando", erro: "" } : l)));
        await mandar(item);
        return;
      }
      setErrosDeRepeticao((atuais) => ({ ...atuais, [item.id]: "" }));
      try {
        await repetirComando(request, item.id);
        if (!vivo.current) return;
        setServidor((atuais) => atuais.map((c) => (c.id === item.id ? { ...c, estado: "pendente", erro: "" } : c)));
        carregar();
      } catch (falha) {
        if (vivo.current) {
          setErrosDeRepeticao((atuais) => ({ ...atuais, [item.id]: falha?.message || "Não deu para tentar de novo." }));
        }
      }
    },
    [carregar, mandar, request],
  );

  const descartar = useCallback((item) => {
    setLocais((atuais) => atuais.filter((l) => l.chave !== item.chave));
  }, []);

  const baloes = useMemo(() => {
    const doServidor = servidor.filter((c) => !ehLeitura(c));
    const todos = [...doServidor, ...enviados.filter((e) => !doServidor.some((c) => c.id === e.id)), ...locais];
    return todos
      .filter((c) => !comandoJaChegou(c, mensagens))
      .map((c) => ({
        ...c,
        chave: c.chave || `comando-${c.id}`,
        situacao: c.local ? c.estado : situacaoDoComando(c) || "enviado",
        erro: (c.id !== undefined && errosDeRepeticao[c.id]) || c.erro || "",
        citada: c.citada || citacoes.current.get(c.id) || (mensagens || []).find((m) => c.citada_id && m.id_externo === c.citada_id) || null,
      }))
      .sort((a, b) => quando(a) - quando(b));
  }, [servidor, enviados, locais, mensagens, errosDeRepeticao]);

  const leituras = servidor.filter(ehLeitura).sort((a, b) => quando(a) - quando(b));
  const ultimaLeitura = leituras[leituras.length - 1] || null;
  const leitura = {
    marcando: Boolean(ultimaLeitura && situacaoDoComando(ultimaLeitura) === "enviando"),
    falhou: ultimaLeitura && situacaoDoComando(ultimaLeitura) === "falhou" ? ultimaLeitura : null,
  };

  return { baloes, erroDaFila, enviar, tentarDeNovo, descartar, recarregar: carregar, leitura };
}
